import { primaryOutline } from "@Components/common/byb-ds/buttons/styled-components";
import { AccountPageLayout, CharacterInputGrid } from "@Components/game/account";
import { PinLockedIconWrapper, PinLockedSection } from "@Components/game/account/styled-components";
import { BybButtonVR } from "@Components/game/common";
import { PinEntryProvider, usePinEntry } from "@Context/common/account/pin-entry-context";
import { faLockKeyhole } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useI18n from "@i18n";
import { Box, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";

const PinDot = ({ filled, active, error }: { filled: boolean; active: boolean; error?: boolean }) => (
  <Box
    sx={(theme) => ({
      width: 96,
      height: 130,
      borderRadius: theme.spacing(2),
      backgroundColor: theme.palette.dsSurface.v100,
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease-in-out",
      ...(error ? { boxShadow: `0 0 0 2px ${theme.palette.dsDestructive.v400}` } : active ? primaryOutline(theme) : {}),
    })}
  >
    {filled && (
      <Box
        sx={(theme) => ({
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: theme.palette.dsBase.v200,
        })}
      />
    )}
  </Box>
);

const PinEntry = () => {
  const { userId } = useParams();

  return (
    <PinEntryProvider userId={userId!}>
      <PinEntryContent />
    </PinEntryProvider>
  );
};

const PinEntryContent = () => {
  const { pin, error, attempts, lockEndTime, handlePinInput, handleBackspace, handleLogout } = usePinEntry();
  const numbers = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
  const { i18n } = useI18n();

  if (attempts === 0) {
    return (
      <AccountPageLayout pageId="PIN_ENTRY" title="" centered>
        <PinLockedSection>
          <PinLockedIconWrapper>
            <FontAwesomeIcon icon={faLockKeyhole} />
          </PinLockedIconWrapper>

          <Stack spacing={1}>
            <Typography variant="text4xl700" color="dsBase.v200">
              {i18n("account.pin.locked.title")}
            </Typography>
            <Typography variant="textBase500" color="dsNeutral.v300" sx={{ maxWidth: 540 }}>
              {lockEndTime && lockEndTime > 0
                ? i18n("account.pin.locked.message.timed", { time: dayjs(lockEndTime).fromNow() })
                : i18n("account.pin.locked.message.generic")}
            </Typography>
          </Stack>
          <BybButtonVR onClick={handleLogout} color="secondary" size="large">
            {i18n("account.pin.locked.button.signout")}
          </BybButtonVR>
        </PinLockedSection>
      </AccountPageLayout>
    );
  }

  return (
    <AccountPageLayout
      pageId="PIN_ENTRY"
      title={i18n("account.pin.entry.vr.title")}
      description={i18n("account.pin.entry.vr.description")}
      sx={{ height: "100%", justifyContent: "space-between", boxSizing: "border-box", pt: 10 }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={3} justifyContent="center">
          {[...Array(4)].map((_, index) => (
            <PinDot key={index} filled={index < pin.length} active={index === pin.length} error={error} />
          ))}
        </Stack>
        {attempts < 3 && (
          <Typography variant="textBase500" color="dsNeutral.v300">
            {i18n("account.pin.entry.attempts", { count: attempts, plural: attempts > 1 ? "s" : "" })}
          </Typography>
        )}
      </Stack>

      <Stack spacing={5} alignItems="center" sx={{ mt: "auto", pb: 6 }}>
        <CharacterInputGrid characters={numbers} onCharacterClick={handlePinInput} onBackspace={handleBackspace} buttonWidth={48} />
      </Stack>
    </AccountPageLayout>
  );
};

export default PinEntry;
